import type { CaptureResult } from 'posthog-js'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useMemo } from 'react'
import posthog from 'posthog-js'
import { PostHogProvider as PostHogNativeProvider, usePostHog } from 'posthog-js/react'

import type { AnalyticsConfig, AnalyticsContext } from './types'
import { getAnalyticsConfig, getBaseProperties } from './config'
import { getPageName } from './utils'

interface AnalyticsProviderProps {
    children: ReactNode
    context: AnalyticsContext
}

const AnalyticsCtx = createContext<AnalyticsContext | null>(null)

const injectPageName = (event: CaptureResult | null): CaptureResult | null => {
    if (!event) return event
    if (event.event !== '$pageview' && event.event !== '$pageleave') return event

    const pathname = String(event.properties.$pathname ?? window.location.pathname)
    event.properties = {
        ...event.properties,
        $pagename: getPageName(pathname),
    }
    return event
}

const buildPostHogOptions = (config: AnalyticsConfig, baseProperties: Record<string, unknown>) => ({
    api_host: config.apiHost,
    debug: config.debug,
    loaded: (instance: typeof posthog) => {
        instance.register(baseProperties)
    },
    persistence: 'localStorage+cookie' as const,
    capture_pageview: 'history_change' as const,
    capture_pageleave: 'if_capture_pageview' as const,
    before_send: injectPageName,
    autocapture: true,
    enable_heatmaps: config.enableHeatmaps,
    person_profiles: 'identified_only' as const,
    on_request_error: config.debug
        ? (error: unknown) => {
              console.warn(
                  '[PostHog] Network request failed (possibly blocked by ad blocker):',
                  error,
              )
          }
        : undefined,
    respect_dnt: true,
})

const PostHogInitializer = ({
    config,
    baseProperties,
}: {
    config: AnalyticsConfig
    baseProperties: Record<string, unknown>
}) => {
    useEffect(() => {
        if (!posthog.__loaded) {
            posthog.init(config.apiKey, buildPostHogOptions(config, baseProperties))
        } else {
            posthog.register(baseProperties)
        }
    }, [
        config.apiKey,
        config.apiHost,
        config.debug,
        config.disabled,
        config.enableHeatmaps,
        baseProperties,
    ])

    return null
}

export const LeanPayAnalyticsProvider = ({ children, context }: AnalyticsProviderProps) => {
    const config = useMemo(
        () => getAnalyticsConfig(context),
        [
            context.apiKey,
            context.apiHost,
            context.disabled,
            context.environment,
            context.enableHeatmaps,
        ],
    )

    const baseProperties = useMemo(
        () => getBaseProperties(context),
        [context.app, context.market, context.environment, context.hostname],
    )

    if (config.disabled) {
        return <>{children}</>
    }

    return (
        <PostHogNativeProvider client={posthog}>
            <AnalyticsCtx.Provider value={context}>
                <PostHogInitializer config={config} baseProperties={baseProperties} />
                {children}
            </AnalyticsCtx.Provider>
        </PostHogNativeProvider>
    )
}

export const useAnalytics = () => {
    return usePostHog()
}

export const useAnalyticsContext = () => {
    const context = useContext(AnalyticsCtx)
    if (!context) {
        throw new Error('useAnalyticsContext must be used within LeanPayAnalyticsProvider')
    }
    return context
}
